/**
 * MODÈLE CANONIQUE de l'outil (F1) — la référence UNIQUE de configuration : types de
 * work-item et leurs CHAMPS, LIGNÉE de génération (quel type engendre quel type), STATUTS
 * de workflow + TRANSITIONS autorisées, et types de LIAISONS. Aujourd'hui c'est une
 * constante (présentée en lecture par l'écran Configuration) ; F1b la rendra PILOTÉE PAR
 * LA DONNÉE (persistée + éditable en administration, consommée par l'UI ET le worker).
 * En attendant, c'est déjà la source unique — on cesse de disperser ces définitions.
 */
import {
  Lightbulb, Compass, Layers, Boxes, LayoutList, ListChecks, Bug,
  Circle, LoaderCircle, Eye, CheckCircle2, Ban, ArrowRight, Link2, Copy,
  type LucideIcon,
} from 'lucide-react'

import type { BacklogKind, BacklogStatus } from '@/api/client'

export type FieldType = 'text' | 'markdown' | 'textarea' | 'criteria' | 'select' | 'number' | 'user'

export interface FieldDef {
  key: string
  label: string
  type: FieldType
  required?: boolean
  /** Le champ tenu pour TITRE de l'item (ex. « Je veux » pour une US, cf. F4). */
  asTitle?: boolean
  hint?: string
}

export interface ItemTypeDef {
  kind: BacklogKind
  label: string
  icon: LucideIcon
  description: string
  fields: FieldDef[]
  /** Lignée : les types d'ENFANTS que ce type peut engendrer (cascade IA). */
  generates: BacklogKind[]
  /** Corps en Markdown + diagrammes Mermaid (idée / cadrage — cf. F6/F7). */
  richBody?: boolean
}

const TITRE: FieldDef = { key: 'title', label: 'Titre', type: 'text', required: true, asTitle: true }
const DESCRIPTION: FieldDef = { key: 'description', label: 'Description', type: 'textarea' }

/**
 * Les types de work-item et la LIGNÉE actuelle : Idée/Cadrage → Epic → Feature → US → Tâche.
 * (Cible F7 : Idée/Initiative → Spécification → Epic ; les specs sont aujourd'hui des
 * artefacts approuvés, pas un kind — la lignée évoluera avec F7.)
 */
export const ITEM_TYPES: ItemTypeDef[] = [
  {
    kind: 'idea', label: 'Idée', icon: Lightbulb, richBody: true,
    description: 'Le point de départ : un besoin brut, cadré puis décliné en travail.',
    fields: [TITRE, { key: 'body', label: 'Texte (Markdown + Mermaid)', type: 'markdown' }],
    generates: ['epic'],
  },
  {
    kind: 'cadrage', label: 'Cadrage', icon: Compass, richBody: true,
    description: 'Le cadrage d\'une idée : problème, valeur, utilisateurs cibles.',
    fields: [TITRE, { key: 'body', label: 'Texte (Markdown + Mermaid)', type: 'markdown' }],
    generates: ['epic'],
  },
  {
    kind: 'epic', label: 'Epic', icon: Layers,
    description: 'Un lot de valeur d\'envergure, décliné en features.',
    fields: [TITRE, DESCRIPTION],
    generates: ['feature'],
  },
  {
    kind: 'feature', label: 'Feature', icon: Boxes,
    description: 'Une capacité livrable, déclinée en user stories.',
    fields: [TITRE, DESCRIPTION],
    generates: ['story'],
  },
  {
    kind: 'story', label: 'User Story', icon: LayoutList,
    description: 'Un besoin utilisateur testable. Cible F4 : « En tant que / Je veux / Afin de ».',
    fields: [
      { key: 'as_a', label: 'En tant que', type: 'text' },
      { key: 'i_want', label: 'Je veux', type: 'text', required: true, asTitle: true,
        hint: 'Sert de titre à la story (F4).' },
      { key: 'so_that', label: 'Afin de', type: 'text' },
      { key: 'acceptance_criteria', label: 'Critères d\'acceptation', type: 'criteria' },
    ],
    generates: ['task'],
  },
  {
    kind: 'task', label: 'Tâche', icon: ListChecks,
    description: 'Une unité d\'exécution technique d\'une story.',
    fields: [TITRE, DESCRIPTION],
    generates: [],
  },
  {
    kind: 'bug', label: 'Bug', icon: Bug,
    description: 'Une anomalie à corriger.',
    fields: [TITRE, DESCRIPTION, { key: 'acceptance_criteria', label: 'Critères', type: 'criteria' }],
    generates: [],
  },
]

export interface StatusDef {
  status: BacklogStatus
  label: string
  icon: LucideIcon
  /** Statuts vers lesquels une transition est autorisée ; la règle est également imposée par l’API. */
  to: BacklogStatus[]
}

/** Statuts de workflow + transitions autorisées (défaut ; l’API impose la configuration effective). */
export const WORKFLOW: StatusDef[] = [
  { status: 'todo', label: 'À faire', icon: Circle, to: ['in_progress'] },
  { status: 'in_progress', label: 'En cours', icon: LoaderCircle, to: ['in_review', 'todo'] },
  { status: 'in_review', label: 'En revue', icon: Eye, to: ['done', 'in_progress'] },
  { status: 'done', label: 'Terminé', icon: CheckCircle2, to: ['in_progress'] },
]

/** État terminal « bloqué » (transverse ; représenté par le badge, hors colonnes du board). */
export const BLOCKED = { label: 'Bloqué', icon: Ban }

export interface LinkTypeDef { key: string; label: string; icon: LucideIcon; description: string }

/** Types de liaisons entre work-items (source: item_links). */
export const LINK_TYPES: LinkTypeDef[] = [
  { key: 'blocks', label: 'Bloque', icon: Ban, description: 'La source empêche l\'avancement de la cible.' },
  { key: 'relates_to', label: 'Lié à', icon: Link2, description: 'Relation informative entre deux items.' },
  { key: 'duplicates', label: 'Duplique', icon: Copy, description: 'La source fait doublon avec la cible.' },
]

/** La lignée, à plat, pour l'affichage « A → B ». */
export const LINEAGE: { from: BacklogKind; to: BacklogKind; icon: LucideIcon }[] =
  ITEM_TYPES.flatMap((t) => t.generates.map((c) => ({ from: t.kind, to: c, icon: ArrowRight })))
